/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {skipToken} from '@reduxjs/toolkit/query';

import {useGetMakeModelWithMakeQuery} from '@dms/api/sales';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import i18n, {FALLBACK_LANGUAGE} from '@dms/i18n';

import type {Nullish} from 'shared/web';

import {toSelectableMakeOptions, toSelectableModelFamilyOptions} from 'features/vehicle-catalogue';

import {t} from './catalogueUi';

// The backend validates keys against the car catalogue only.
const VEHICLE_TYPE = VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR;
const language = () => [i18n.resolvedLanguage ?? FALLBACK_LANGUAGE];

type CatalogueOption = {value: string; label: string};

/**
 * The empty option stands for an entry of the tenant's own; a stored key the catalogue does not
 * list stays selectable so the record still renders and saves it unchanged. It is only called
 * legacy once the catalogue has actually answered.
 */
function withOwnAndStored(
  ownLabel: string,
  options: CatalogueOption[],
  storedKey: string | Nullish,
  hasCatalogue: boolean
) {
  const isStoredMissing = storedKey && !options.some((option) => option.value === storedKey);
  return [
    {value: '', label: ownLabel},
    ...options,
    ...(isStoredMissing
      ? [
          {
            value: storedKey,
            label: hasCatalogue
              ? t(
                  'entity.newVehicle.catalogue.legacyVehicleDataKey',
                  '{key} (not in the vehicle catalogue)',
                  {key: storedKey}
                )
              : storedKey,
          },
        ]
      : []),
  ];
}

/** Official car makes a catalogue make can be linked to, without the MAKE_OTHER placeholder. */
export function useVehicleCatalogueMakeOptions(storedKey?: string | Nullish) {
  const query = useGetMakeModelWithMakeQuery({vehicleType: VEHICLE_TYPE, lang: language()});
  return {
    options: withOwnAndStored(
      t('entity.newVehicle.catalogue.ownMake', 'Own make (not in the vehicle catalogue)'),
      toSelectableMakeOptions(query.data),
      storedKey,
      query.isSuccess && !query.isFetching
    ),
    isLoading: query.isFetching,
    isError: query.isError,
  };
}

/** Model families of one catalogue make, without the -MODELFAMILY_OTHER placeholder. */
export function useVehicleCatalogueModelOptions(
  makeKey: string | Nullish,
  storedKey?: string | Nullish
) {
  // currentData: while another make loads, `data` still holds the previous make's families.
  const query = useGetMakeModelWithMakeQuery(
    makeKey ? {vehicleType: VEHICLE_TYPE, make: makeKey, lang: language()} : skipToken
  );
  const families = toSelectableModelFamilyOptions(
    query.currentData?.filter((entry) => entry.make === makeKey)
  );
  return {
    options: withOwnAndStored(
      t('entity.newVehicle.catalogue.ownModel', 'Own model (not in the vehicle catalogue)'),
      families,
      storedKey,
      query.isSuccess && !query.isFetching
    ),
    isLoading: query.isFetching,
    isError: query.isError,
  };
}

export const optionLabel = (options: CatalogueOption[], value: string) =>
  options.find((option) => option.value === value)?.label;
